/* İçerik editörü — /yonetim. Site herkese açık, burası ADMIN_PIN ister. */

import { useCallback, useEffect, useState } from "react";
import type { SiteContent } from "../../shared/types.js";
import { api, ApiError } from "../api.js";
import { TranslateCtx } from "./fields.js";
import {
  DressCodeEditor,
  EveningEditor,
  EventEditor,
  SessionsEditor,
  SpeakersEditor,
  WorkshopsEditor,
  type SectionKey,
} from "./sections.js";

const SECTIONS: { key: SectionKey; label: string }[] = [
  { key: "sessions", label: "Program" },
  { key: "workshops", label: "Atölyeler" },
  { key: "speakers", label: "Sahne alanlar" },
  { key: "dressCode", label: "Dress code" },
  { key: "evening", label: "Akşam" },
  { key: "event", label: "Etkinlik" },
];

export function AdminApp() {
  const [authed, setAuthed] = useState<boolean | null>(null);
  const [pin, setPin] = useState("");
  const [error, setError] = useState("");
  const [draft, setDraft] = useState<SiteContent | null>(null);
  const [section, setSection] = useState<SectionKey>("sessions");
  const [dirty, setDirty] = useState(false);
  const [toast, setToast] = useState("");
  const [canTranslate, setCanTranslate] = useState(false);

  useEffect(() => {
    api
      .adminMe()
      .then((r) => {
        setAuthed(r.admin);
        setCanTranslate(r.translate);
      })
      .catch(() => setAuthed(false));
  }, []);

  useEffect(() => {
    if (!authed) return;
    api.content().then(setDraft).catch(() => setError("İçerik yüklenemedi."));
  }, [authed]);

  /* Kaydedilmemiş değişiklikle sekmeyi kapatmaya karşı uyarı */
  useEffect(() => {
    if (!dirty) return;
    const handler = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty]);

  const flash = useCallback((msg: string) => {
    setToast(msg);
    window.setTimeout(() => setToast((cur) => (cur === msg ? "" : cur)), 2600);
  }, []);

  const login = async () => {
    setError("");
    try {
      await api.adminLogin(pin);
      setPin("");
      setAuthed(true);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Giriş yapılamadı.");
    }
  };

  const patch = <K extends SectionKey>(key: K, value: SiteContent[K]) => {
    setDraft((cur) => (cur ? { ...cur, [key]: value } : cur));
    setDirty(true);
  };

  const save = async () => {
    if (!draft) return;
    try {
      const r = await api.saveSection(section, draft[section]);
      setDraft(r.content);
      setDirty(false);
      flash("Kaydedildi — site güncellendi.");
    } catch (e) {
      flash(e instanceof ApiError ? e.message : "Kaydedilemedi.");
    }
  };

  if (authed === null) {
    return <Center>Yükleniyor…</Center>;
  }

  if (!authed) {
    return (
      <div className="admin">
        <div className="wrap" style={{ maxWidth: 380, paddingTop: 90 }}>
          <h1 className="serif" style={{ fontSize: 30, margin: 0 }}>
            İçerik editörü
          </h1>
          <p className="dim" style={{ fontSize: 15 }}>
            Program ve metinleri buradan güncelleyebilirsiniz.
          </p>
          <label className="field">
            <span className="field__label">Yönetim kodu</span>
            <input
              className="input"
              type="password"
              autoComplete="current-password"
              value={pin}
              onChange={(e) => {
                setPin(e.target.value);
                setError("");
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") void login();
              }}
            />
          </label>
          {error && <p style={{ color: "#d8695e", fontSize: 14.5 }}>{error}</p>}
          <button
            type="button"
            className="btn btn--gold"
            style={{ width: "100%", marginTop: 12 }}
            onClick={() => void login()}
          >
            Gir
          </button>
          <p style={{ marginTop: 28 }}>
            <a href="/">← Siteye dön</a>
          </p>
        </div>
      </div>
    );
  }

  if (!draft) return <Center>{error || "İçerik yükleniyor…"}</Center>;

  return (
    <TranslateCtx.Provider value={canTranslate}>
      <div className="admin">
        <div
          className="topbar"
          style={{ background: "rgba(7,12,30,.95)" }}
        >
          <div className="topbar__inner" style={{ maxWidth: 900 }}>
            <strong className="serif" style={{ fontSize: 18 }}>
              İçerik editörü
            </strong>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <a className="btn btn--ghost" href="/" style={{ textDecoration: "none" }}>
                Site
              </a>
              <button
                type="button"
                className="btn btn--gold"
                onClick={() => void save()}
                disabled={!dirty}
                style={dirty ? undefined : { opacity: 0.5 }}
              >
                Kaydet
              </button>
            </div>
          </div>
        </div>

        <div className="wrap" style={{ paddingTop: 18 }}>
          <div style={{ display: "flex", gap: 8, overflowX: "auto", paddingBottom: 14 }}>
            {SECTIONS.map((s) => (
              <button
                key={s.key}
                type="button"
                className={"btn " + (section === s.key ? "btn--gold" : "btn--ghost")}
                onClick={() => setSection(s.key)}
              >
                {s.label}
              </button>
            ))}
          </div>

          {dirty && (
            <p className="dim" style={{ fontSize: 14.5, margin: "0 0 8px" }}>
              Kaydedilmemiş değişiklik var — “Kaydet” bu bölümü yayına alır.
            </p>
          )}

          {section === "sessions" && (
            <SessionsEditor
              value={draft.sessions}
              speakers={draft.speakers}
              onChange={(v) => patch("sessions", v)}
            />
          )}
          {section === "workshops" && (
            <WorkshopsEditor value={draft.workshops} onChange={(v) => patch("workshops", v)} />
          )}
          {section === "speakers" && (
            <SpeakersEditor value={draft.speakers} onChange={(v) => patch("speakers", v)} />
          )}
          {section === "dressCode" && (
            <DressCodeEditor value={draft.dressCode} onChange={(v) => patch("dressCode", v)} />
          )}
          {section === "evening" && (
            <EveningEditor value={draft.evening} onChange={(v) => patch("evening", v)} />
          )}
          {section === "event" && (
            <EventEditor value={draft.event} onChange={(v) => patch("event", v)} />
          )}

          <hr className="rule" style={{ marginTop: 40 }} />
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <a
              className="btn btn--ghost"
              href="/api/admin/backup"
              download="bp-icerik-yedek.json"
              style={{ textDecoration: "none" }}
            >
              JSON yedeği indir
            </a>
            <button
              type="button"
              className="btn btn--danger"
              onClick={() => {
                if (!window.confirm("Tüm içerik varsayılana dönecek. Emin misiniz?")) return;
                api
                  .reset()
                  .then((r) => {
                    setDraft(r.content);
                    setDirty(false);
                    flash("Varsayılan içerik geri yüklendi.");
                  })
                  .catch(() => flash("Sıfırlanamadı."));
              }}
            >
              Varsayılana dön
            </button>
            <button
              type="button"
              className="btn btn--ghost"
              onClick={() => {
                api.adminLogout().then(() => setAuthed(false));
              }}
            >
              Çıkış
            </button>
          </div>
        </div>

        {toast && (
          <div
            role="status"
            style={{
              position: "fixed",
              left: "50%",
              bottom: 24,
              transform: "translateX(-50%)",
              background: "rgba(6,11,30,.95)",
              border: "1px solid var(--line)",
              borderRadius: 4,
              padding: "12px 18px",
              fontSize: 15,
              color: "var(--sand)",
            }}
          >
            {toast}
          </div>
        )}
      </div>
    </TranslateCtx.Provider>
  );
}

function Center({ children }: { children: React.ReactNode }) {
  return (
    <div className="admin">
      <p className="center dim" style={{ paddingTop: 90 }}>
        {children}
      </p>
    </div>
  );
}
